import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  Paper,
  Popper,
  ClickAwayListener,
} from '@mui/material';

export function TeamSelector({
  label,
  selectedTeams = [],
  availableTeams = [],
  onChange,
  onSelectAll,
}) {
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const filteredTeams = availableTeams
    .filter((team) => team.name.toLowerCase().includes(searchText.toLowerCase())
      && !selectedTeams.some((selected) => selected.id === team.id));

  const handleInputClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleSelectTeam = (team) => {
    const newSelected = [...selectedTeams, team];
    onChange(newSelected);
    setSearchText('');
  };

  const handleRemoveTeam = (teamId) => {
    const newSelected = selectedTeams.filter((t) => t.id !== teamId);
    onChange(newSelected);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setSearchText('');
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
      }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '500',
          color: '#333',
        }}
        >
          {label}
        </Box>
        {onSelectAll && (
          <Button
            onClick={onSelectAll}
            sx={{
              fontSize: '12px',
              color: '#53A551',
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto',
            }}
          >
            Select All
          </Button>
        )}
      </Box>

      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{
          position: 'relative',
          backgroundColor: '#F8F8F8',
          border: '1.5px solid #E0E1E6',
        }}
        >
          <Box
            onClick={handleInputClick}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              minHeight: '56px',
              maxHeight: '120px',
              padding: '8px 12px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 0.5,
              overflow: 'auto',
              cursor: 'text',
              '&:hover': {
                borderColor: '#999',
              },
              '&:focus-within': {
                borderColor: '#1976d2',
                borderWidth: '2px',
              },
            }}
          >
            {/* Selected team chips */}
            {selectedTeams.map((team) => (
              <Chip
                key={team.id}
                label={team.name}
                onDelete={() => handleRemoveTeam(team.id)}
                size="small"
                sx={{
                  height: '28px',
                  fontSize: '13px',
                  maxWidth: 'none',
                  backgroundColor: '#FBFBFC',
                  border: '1px solid #E2E2E2',
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: '18px',
                  },
                }}
              />
            ))}

            {/* Search input */}
            <TextField
              variant="standard"
              placeholder={selectedTeams.length === 0 ? `Search and select ${label.toLowerCase()}...` : ''}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                minWidth: '150px',
                flexGrow: 1,
                '& .MuiInput-underline:before': {
                  display: 'none',
                },
                '& .MuiInput-underline:after': {
                  display: 'none',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '4px 0',
                },
              }}
            />
          </Box>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ width: anchorEl?.clientWidth, zIndex: 1300 }}
          >
            <Paper elevation={3} sx={{ maxHeight: 200, overflow: 'auto' }}>
              <List dense>
                {filteredTeams.length > 0 ? (
                  filteredTeams.map((team) => (
                    <ListItem
                      key={team.id}
                      button
                      onClick={() => handleSelectTeam(team)}
                      sx={{ fontSize: '14px' }}
                    >
                      <ListItemText
                        primary={team.name}
                        primaryTypographyProps={{ fontSize: '14px' }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No teams found"
                      primaryTypographyProps={{ fontSize: '14px', color: '#666' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
}

TeamSelector.propTypes = {
  label: PropTypes.string.isRequired,
  selectedTeams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  availableTeams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func,
};

TeamSelector.defaultProps = {
  selectedTeams: [],
  availableTeams: [],
  onSelectAll: null,
};

export default TeamSelector;
